import React from 'react';
import type { CandidateEngagementDetails } from '@axiom/validation';
import { CandidateEngagementsConst } from '@axiom/const';
import { Layout, LayoutItem } from '@axiom/ui';

import { EngagementsViewStateBannerWarmed } from './EngagementsViewStateBannerWarmed';
import { EngagementsViewStateBannerWarmedYes } from './EngagementsViewStateBannerWarmedYes';
import { EngagementsViewStateBannerInterested } from './EngagementsViewStateBannerInterested';
import { EngagementsViewStateBannerSubmitted } from './EngagementsViewStateBannerSubmitted';
import { EngagementsViewStateBannerInterviewingExpectedExternal } from './EngagementsViewStateBannerInterviewingExpectedExternal';
import { EngagementsViewStateBannerInterviewingExpectedManual } from './EngagementsViewStateBannerInterviewingExpectedManual';
import { EngagementsViewStateBannerInterviewed } from './EngagementsViewStateBannerInterviewed';
import { EngagementsViewStateBannerInterviewingCancelled } from './EngagementsViewStateBannerInterviewingCancelled';
import { EngagementsViewStateBannerPerInterview } from './EngagementsViewStateBannerPerInterview';

export type EngagementsViewStateBannerProps = {
  engagement: CandidateEngagementDetails;
  onWarmedNo: () => void;
  onWarmedYes: () => void;
  name?: string;
};

export const EngagementsViewStateBanner: React.FC<
  EngagementsViewStateBannerProps
> = ({ engagement, onWarmedNo, onWarmedYes, name }) => {
  switch (engagement.engagementState) {
    case CandidateEngagementsConst.EngagementStates.Warmed: {
      return (
        <EngagementsViewStateBannerWarmed
          name="ENGAGEMENTS_VIEW_STATE_BANNER_WARMED"
          engagement={engagement}
          onWarmedNo={onWarmedNo}
          onWarmedYes={onWarmedYes}
        />
      );
    }
    case CandidateEngagementsConst.EngagementStates.WarmedYes: {
      return (
        <EngagementsViewStateBannerWarmedYes
          name="ENGAGEMENTS_VIEW_STATE_BANNER_WARMED_YES"
          engagement={engagement}
        />
      );
    }
    case CandidateEngagementsConst.EngagementStates.Interested: {
      return (
        <EngagementsViewStateBannerInterested name="ENGAGEMENTS_VIEW_STATE_BANNER_INTERESTED" />
      );
    }
    case CandidateEngagementsConst.EngagementStates.Submitted: {
      return (
        <EngagementsViewStateBannerSubmitted
          name="ENGAGEMENTS_VIEW_STATE_BANNER_SUBMITTED"
          engagement={engagement}
        />
      );
    }
    // These three are the outputs of the BE's mostRestrictiveState rollup — the only
    // rollup states where engagement.interviews[] is populated. Fan out one banner per
    // interview, picking the child via interview.interviewingState rather than the rollup.
    case CandidateEngagementsConst.EngagementStates.InterviewingUnaccepted:
    case CandidateEngagementsConst.EngagementStates
      .InterviewingClientUnacceptedTalentAccepted:
    case CandidateEngagementsConst.EngagementStates.InterviewingConfirmed: {
      return (
        <Layout direction="vertical" verticalGutter="16px">
          {engagement.interviews.map((interview, index) => (
            <LayoutItem key={`${interview.startTime}-${interview.endTime}`}>
              <EngagementsViewStateBannerPerInterview
                name={`ENGAGEMENTS_VIEW_STATE_BANNER_PER_INTERVIEW_${index}`}
                engagement={engagement}
                interview={interview}
              />
            </LayoutItem>
          ))}
        </Layout>
      );
    }
    case CandidateEngagementsConst.EngagementStates
      .InterviewingExpectedExternal: {
      return (
        <EngagementsViewStateBannerInterviewingExpectedExternal
          name="ENGAGEMENTS_VIEW_STATE_BANNER_INTERVIEWING_EXPECTED_EXTERNAL"
          engagement={engagement}
        />
      );
    }
    case CandidateEngagementsConst.EngagementStates
      .InterviewingExpectedManual: {
      return (
        <EngagementsViewStateBannerInterviewingExpectedManual
          name="ENGAGEMENTS_VIEW_STATE_BANNER_INTERVIEWING_EXPECTED_MANUAL"
          engagement={engagement}
        />
      );
    }
    case CandidateEngagementsConst.EngagementStates.Interviewed: {
      return (
        <EngagementsViewStateBannerInterviewed
          name="ENGAGEMENTS_VIEW_STATE_BANNER_INTERVIEWED"
          engagement={engagement}
        />
      );
    }
    case CandidateEngagementsConst.EngagementStates.InterviewingCancelled:
    case CandidateEngagementsConst.EngagementStates
      .InterviewingTalentDeclined: {
      return (
        <EngagementsViewStateBannerInterviewingCancelled
          name="ENGAGEMENTS_VIEW_STATE_BANNER_INTERVIEWING_CANCELLED"
          engagement={engagement}
        />
      );
    }
    default: {
      return null;
    }
  }
};
